import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type OnboardingStep = {
  completed: boolean;
  id: string;
  isVisible: boolean;
  path: string;
};

export type OnboardingTrial = {
  completed: boolean;
};

export type OnboardingSteps = {
  [stepId: string]: OnboardingStep;
};

export type OnboardingTrials = {
  [trialId: string]: OnboardingTrial;
};

type Onboarding = { steps: OnboardingSteps; trials: OnboardingTrials };

interface OnboardingsState {
  onboardings: {
    [userId: string]: Onboarding;
  };
  setDefaultOnboardingSteps: (userId: string, steps: OnboardingSteps) => void;
  setDefaultOnboardingTrial: (userId: string, trialId: string) => void;
  setOnboardingStepCompleted: (userId: string, stepId: string) => void;
  setOnboardingStepVisible: (userId: string, stepId: string) => void;
  setOnboardingTrialCompleted: (userId: string, trialId: string) => void;
}

export const useOnboardingsStore = create<OnboardingsState>()(
  persist(
    (set) => ({
      onboardings: {},
      setDefaultOnboardingSteps: (userId: string, steps: OnboardingSteps) =>
        set(({ onboardings }) => ({
          onboardings: {
            ...onboardings,
            [userId]: { steps, trials: {} } as Onboarding,
          },
        })),
      setOnboardingStepCompleted: (userId: string, stepId: string) =>
        set(({ onboardings }) => ({
          onboardings: {
            ...onboardings,
            [userId]: {
              ...onboardings[userId],
              steps: {
                ...onboardings[userId].steps,
                [stepId]: { ...onboardings[userId].steps[stepId], completed: true },
              },
            },
          },
        })),
      setOnboardingStepVisible: (userId: string, stepId: string) =>
        set(({ onboardings }) => ({
          onboardings: {
            ...onboardings,
            [userId]: {
              ...onboardings[userId],
              steps: {
                ...onboardings[userId].steps,
                [stepId]: { ...onboardings[userId].steps[stepId], isVisible: true },
              },
            },
          },
        })),
      setDefaultOnboardingTrial: (userId: string, trialId: string) =>
        set(({ onboardings }) => ({
          onboardings: {
            ...onboardings,
            [userId]: {
              ...onboardings[userId],
              trials: {
                ...onboardings[userId].trials,
                [trialId]: { completed: false },
              },
            },
          },
        })),
      setOnboardingTrialCompleted: (userId: string, trialId: string) =>
        set(({ onboardings }) => ({
          onboardings: {
            ...onboardings,
            [userId]: {
              ...onboardings[userId],
              trials: {
                ...onboardings[userId].trials,
                [trialId]: { completed: true },
              },
            },
          },
        })),
    }),
    {
      name: 'onboardings',
    },
  ),
);
